import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient, apiErrorMessage } from '@/lib/api'
import type { DimensionScores, OptimizeResponse, SkillDetail, TestRun } from '@/lib/types'

export default function SkillDetailPage() {
  const { id = '' } = useParams()
  const qc = useQueryClient()
  const [optimizeResult, setOptimizeResult] = useState<OptimizeResponse | null>(null)

  const query = useQuery({
    queryKey: ['skill', id],
    queryFn: async () => (await apiClient.get<SkillDetail>(`/api/v1/skills/${id}`)).data,
    enabled: Boolean(id),
  })

  const validateMut = useMutation({
    mutationFn: async () => {
      const skill = query.data!
      const payload = {
        guardrails: skill.guardrails.map((g) => ({ id: g.id, validated: true })),
      }
      return (await apiClient.put<SkillDetail>(`/api/v1/skills/${id}/guardrails`, payload)).data
    },
    onSuccess: (data) => qc.setQueryData(['skill', id], data),
  })

  const testMut = useMutation({
    mutationFn: async () => {
      return (await apiClient.post<TestRun>(`/api/v1/skills/${id}/test`)).data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['skill', id] })
    },
  })

  const certMut = useMutation({
    mutationFn: async () => {
      return (await apiClient.post<SkillDetail>(`/api/v1/skills/${id}/certify`)).data
    },
    onSuccess: (data) => qc.setQueryData(['skill', id], data),
  })

  const approvalMut = useMutation({
    mutationFn: async () => {
      return (await apiClient.post<SkillDetail>(`/api/v1/skills/${id}/certification-approval`)).data
    },
    onSuccess: (data) => qc.setQueryData(['skill', id], data),
  })

  const optimizeMut = useMutation({
    mutationFn: async () => {
      return (await apiClient.post<OptimizeResponse>(`/api/v1/skills/${id}/optimize`)).data
    },
    onSuccess: (data) => setOptimizeResult(data),
  })

  async function doExport(format: string) {
    const r = await apiClient.get(`/api/v1/skills/${id}/export?format=${format}`, {
      responseType: 'text',
    })
    const blob = new Blob([r.data as string], { type: 'application/octet-stream' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${query.data?.name ?? 'skill'}.${format}`
    a.click()
    URL.revokeObjectURL(url)
  }

  if (query.isLoading) return <p className="text-sm text-neutral-500">Loading…</p>
  if (query.error || !query.data)
    return <p className="text-sm text-red-600">Failed to load skill</p>

  const s = query.data
  const latestTest = s.testRuns[0]
  const allValidated = s.guardrails.length > 0 && s.guardrails.every((g) => g.validated)

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-xs text-neutral-500">
            <span>v{s.version}</span>
            <span>·</span>
            <span>{s.domain}</span>
            <span>·</span>
            <span>risk {s.riskLevel}</span>
            <span>·</span>
            <span className="uppercase tracking-wide">{s.status}</span>
          </div>
          <h1 className="text-2xl font-semibold">{s.name}</h1>
          <p className="text-sm text-neutral-600">{s.description}</p>
        </div>
        <div className="flex flex-col items-end gap-2 shrink-0">
          <div className="flex gap-2 text-xs">
            <button
              onClick={() => doExport('json')}
              className="border rounded px-2 py-1 hover:bg-neutral-50"
            >
              JSON
            </button>
            <button
              onClick={() => doExport('yaml')}
              className="border rounded px-2 py-1 hover:bg-neutral-50"
            >
              YAML
            </button>
            <button
              onClick={() => doExport('claude')}
              className="border rounded px-2 py-1 hover:bg-neutral-50"
            >
              Claude
            </button>
            <button
              onClick={() => doExport('markdown')}
              className="border rounded px-2 py-1 hover:bg-neutral-50"
            >
              Markdown
            </button>
          </div>
        </div>
      </header>

      <section className="rounded border bg-white p-4">
        <h2 className="font-semibold mb-2">Scope</h2>
        <ul className="text-sm list-disc pl-5 space-y-0.5">
          {s.scope.map((x) => <li key={x}>{x}</li>)}
        </ul>
        <h2 className="font-semibold mt-4 mb-2">Out of scope</h2>
        <ul className="text-sm list-disc pl-5 space-y-0.5">
          {s.outOfScope.map((x) => <li key={x}>{x}</li>)}
        </ul>
      </section>

      <section className="rounded border bg-white p-4">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold">Guardrails ({s.guardrails.length})</h2>
          {s.status === 'GUARDRAILS_PENDING' && !allValidated && (
            <button
              onClick={() => validateMut.mutate()}
              disabled={validateMut.isPending}
              className="rounded bg-neutral-900 text-white text-xs px-3 py-1.5 disabled:opacity-50"
            >
              {validateMut.isPending ? 'Validating…' : 'Mark all validated'}
            </button>
          )}
        </div>
        {validateMut.error && (
          <p className="mt-2 text-xs text-red-600">{apiErrorMessage(validateMut.error)}</p>
        )}
        <ul className="mt-3 space-y-2">
          {s.guardrails.map((g) => (
            <li key={g.id} className="rounded border p-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="font-medium">{g.type}</span>
                <span
                  className={
                    g.validated
                      ? 'text-xs text-green-700'
                      : 'text-xs text-neutral-500'
                  }
                >
                  {g.validated ? 'validated' : 'pending'}
                </span>
              </div>
              <p className="mt-1 text-neutral-700">{g.description}</p>
            </li>
          ))}
        </ul>
      </section>

      <section className="rounded border bg-white p-4">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold">Tests</h2>
          <div className="flex gap-2">
            {(s.status === 'TESTING' || s.status === 'CERTIFIED') && (
              <>
                <button
                  onClick={() => optimizeMut.mutate()}
                  disabled={optimizeMut.isPending}
                  className="rounded border text-xs px-3 py-1.5 disabled:opacity-50 hover:bg-neutral-50"
                >
                  {optimizeMut.isPending ? 'Analyzing…' : 'Optimize'}
                </button>
                <button
                  onClick={() => testMut.mutate()}
                  disabled={testMut.isPending}
                  className="rounded bg-neutral-900 text-white text-xs px-3 py-1.5 disabled:opacity-50"
                >
                  {testMut.isPending ? 'Running test…' : 'Run test with Claude'}
                </button>
              </>
            )}
          </div>
        </div>
        {testMut.error && (
          <p className="mt-2 text-xs text-red-600">{apiErrorMessage(testMut.error)}</p>
        )}
        {optimizeMut.error && (
          <p className="mt-2 text-xs text-red-600">{apiErrorMessage(optimizeMut.error)}</p>
        )}
        {latestTest ? (
          <div className="mt-3 space-y-3">
            <p className="text-sm">
              Latest:{' '}
              <span className="font-medium">{latestTest.status}</span>{' '}
              — {(latestTest.score * 100).toFixed(0)}% ({latestTest.passCount}/
              {latestTest.passCount + latestTest.failCount})
            </p>
            {latestTest.dimensionScores && (
              <DimensionBreakdown scores={latestTest.dimensionScores} />
            )}
          </div>
        ) : (
          <p className="mt-3 text-sm text-neutral-500">No test runs yet.</p>
        )}
        {optimizeResult && (
          <div className="mt-4 rounded border border-amber-200 bg-amber-50 p-3 space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-amber-900">Optimization Suggestions</h3>
              <span className="text-xs text-amber-700">
                Est. score after: {optimizeResult.estimatedScoreAfterOptimization}/100
              </span>
            </div>
            {optimizeResult.suggestions.map((s, i) => (
              <div key={i} className="rounded border border-amber-200 bg-white p-2 text-sm">
                <div className="flex items-center gap-2">
                  <span className={`inline-block w-2 h-2 rounded-full ${
                    s.priority === 'high' ? 'bg-red-500' : s.priority === 'medium' ? 'bg-amber-500' : 'bg-green-500'
                  }`} />
                  <span className="font-medium">{s.title}</span>
                  <span className="text-xs text-neutral-500">
                    {s.currentScore !== null ? `${s.currentScore}/${s.maxScore}` : `—/${s.maxScore}`}
                  </span>
                </div>
                <p className="mt-1 text-neutral-600">{s.description}</p>
                <div className="mt-2 text-xs bg-neutral-50 rounded p-2 font-mono">
                  <div><span className="text-neutral-500">field:</span> {s.suggestedChanges.field}</div>
                  <div className="text-red-700">- {s.suggestedChanges.current}</div>
                  <div className="text-green-700">+ {s.suggestedChanges.suggested}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="rounded border bg-white p-4">
        <h2 className="font-semibold">Certification</h2>
        {s.status === 'CERTIFIED' ? (
          <p className="mt-2 text-sm text-green-700">
            Certified on {new Date(s.certifiedAt!).toLocaleString()}.
          </p>
        ) : (
          <div className="mt-3 flex flex-wrap gap-2">
            {s.workspace.requireCertificationApproval && (
              <button
                onClick={() => approvalMut.mutate()}
                disabled={approvalMut.isPending || s.status !== 'TESTING'}
                className="rounded border text-xs px-3 py-1.5 disabled:opacity-50"
              >
                {approvalMut.isPending ? 'Recording approval…' : 'Record approval'}
              </button>
            )}
            <button
              onClick={() => certMut.mutate()}
              disabled={certMut.isPending || s.status !== 'TESTING'}
              className="rounded bg-neutral-900 text-white text-xs px-3 py-1.5 disabled:opacity-50"
            >
              {certMut.isPending ? 'Certifying…' : 'Certify'}
            </button>
          </div>
        )}
        {certMut.error && (
          <p className="mt-2 text-xs text-red-600">{apiErrorMessage(certMut.error)}</p>
        )}
      </section>
    </div>
  )
}

const DIMENSION_LABELS: Record<string, string> = {
  schemaQuality: 'Schema Quality',
  workflowClarity: 'Workflow Clarity',
  boundaryCoverage: 'Boundary Coverage',
  checkpointDesign: 'Checkpoint Design',
  instructionSpecificity: 'Instruction Specificity',
  resourceIntegration: 'Resource Integration',
  architectureQuality: 'Architecture Quality',
  realWorldPerformance: 'Real-world Performance',
}

function DimensionBreakdown({ scores }: { scores: DimensionScores }) {
  const dims = Object.entries(DIMENSION_LABELS)
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-xs text-neutral-500">
        <span>Dimension Breakdown</span>
        <span className="font-medium text-neutral-900">
          {scores.totalScore}/{scores.maxTotalScore}
        </span>
      </div>
      {dims.map(([key, label]) => {
        const dim = scores[key as keyof DimensionScores]
        if (!dim || typeof dim !== 'object' || !('score' in dim)) return null
        const pct = (dim.score / dim.maxScore) * 100
        return (
          <div key={key} className="group" title={dim.reasoning}>
            <div className="flex items-center justify-between text-xs">
              <span className="text-neutral-700">{label}</span>
              <span className="tabular-nums font-medium">
                {dim.score}/{dim.maxScore}
              </span>
            </div>
            <div className="h-1.5 rounded-full bg-neutral-100 mt-0.5">
              <div
                className={`h-1.5 rounded-full transition-all ${
                  pct >= 80 ? 'bg-green-500' : pct >= 60 ? 'bg-amber-500' : 'bg-red-500'
                }`}
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        )
      })}
    </div>
  )
}
